import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionHead } from "./Reveal";
import { PHONE, PHONE_LINK, WHATSAPP_LINK, SERVICE_OPTIONS, EMPLOYEE_OPTIONS } from "@/data/content";
import { MapPin, Phone, MessageCircle, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", company: "", mobile: "", email: "", city: "", employees: "", service: "", message: "" };

const Field = ({ label, children, testid }) => (
    <label className="block" data-testid={testid}>
        <span className="block mb-2 font-mono2 text-[10px] tracking-[0.2em] uppercase text-slate-400">{label}</span>
        {children}
    </label>
);

const inputCls =
    "w-full bg-[#0A1128] border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-400 transition-colors";

const Contact = () => {
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            toast.success("Enquiry submitted. Our compliance team will contact you shortly.");
            setForm(EMPTY);
        } catch (err) {
            toast.error("Could not submit your enquiry. Please call or WhatsApp us directly.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" className="py-24 sm:py-32 border-t border-white/10 bg-[#0D1730]">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid lg:grid-cols-5 gap-14">
                    <div className="lg:col-span-2">
                        <SectionHead
                            eyebrow="Contact Us"
                            title="Let's Simplify Your Compliance"
                            sub="Tell us about your business and the compliance support you need. We respond to every enquiry."
                        />
                        <Reveal delay={0.1}>
                            <div className="mt-10 space-y-6" data-testid="contact-info">
                                <div className="flex items-start gap-4">
                                    <span className="w-10 h-10 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center shrink-0">
                                        <User size={17} className="text-sky-400" />
                                    </span>
                                    <div>
                                        <p className="font-heading font-semibold text-white">Hardik Patel</p>
                                        <p className="text-sm text-slate-400">Pramukh Compliance Services — Ahmedabad</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <span className="w-10 h-10 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center shrink-0">
                                        <MapPin size={17} className="text-sky-400" />
                                    </span>
                                    <div>
                                        <p className="font-heading font-semibold text-white">Pramukh Compliance Services</p>
                                        <p className="text-sm text-slate-400">Ahmedabad, Gujarat, India</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <span className="w-10 h-10 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center shrink-0">
                                        <Phone size={17} className="text-sky-400" />
                                    </span>
                                    <div>
                                        <p className="font-heading font-semibold text-white font-mono2 tracking-wider">{PHONE}</p>
                                        <p className="text-sm text-slate-400">Call or WhatsApp</p>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="mt-9 flex flex-wrap gap-3">
                                <a
                                    href={PHONE_LINK}
                                    data-testid="contact-call-btn"
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors"
                                >
                                    <Phone size={15} /> Call Now
                                </a>
                                <a
                                    href={WHATSAPP_LINK}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-whatsapp-btn"
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-colors"
                                >
                                    <MessageCircle size={15} /> WhatsApp Us
                                </a>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.12} className="lg:col-span-3">
                        <form onSubmit={submit} className="border border-white/10 bg-[#0A1128] p-7 sm:p-10" data-testid="enquiry-form">
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field label="Name *" testid="field-name">
                                    <input required data-testid="enquiry-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
                                </Field>
                                <Field label="Company Name" testid="field-company">
                                    <input data-testid="enquiry-company-input" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company / firm name" />
                                </Field>
                                <Field label="Mobile Number *" testid="field-mobile">
                                    <input required data-testid="enquiry-mobile-input" className={inputCls} value={form.mobile} onChange={set("mobile")} placeholder="10-digit mobile" />
                                </Field>
                                <Field label="Email *" testid="field-email">
                                    <input required type="email" data-testid="enquiry-email-input" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
                                </Field>
                                <Field label="City" testid="field-city">
                                    <input data-testid="enquiry-city-input" className={inputCls} value={form.city} onChange={set("city")} placeholder="Ahmedabad" />
                                </Field>
                                <Field label="Number of Employees" testid="field-employees">
                                    <select data-testid="enquiry-employees-select" className={inputCls} value={form.employees} onChange={set("employees")}>
                                        <option value="">Select range</option>
                                        {EMPLOYEE_OPTIONS.map((o) => (
                                            <option key={o} value={o}>{o}</option>
                                        ))}
                                    </select>
                                </Field>
                                <div className="sm:col-span-2">
                                    <Field label="Required Service" testid="field-service">
                                        <select data-testid="enquiry-service-select" className={inputCls} value={form.service} onChange={set("service")}>
                                            <option value="">Select Service</option>
                                            {SERVICE_OPTIONS.map((o) => (
                                                <option key={o} value={o}>{o}</option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>
                                <div className="sm:col-span-2">
                                    <Field label="Message" testid="field-message">
                                        <textarea data-testid="enquiry-message-input" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Briefly describe your compliance requirement" />
                                    </Field>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="enquiry-form-submit-button"
                                className="mt-7 w-full sm:w-auto px-9 py-3.5 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold text-sm transition-colors"
                            >
                                {loading ? "Submitting…" : "Submit Enquiry"}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default Contact;
